// src/pages/dashboard/Support.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Filter, Search, Clock, AlertCircle, ChevronRight, X, Paperclip } from 'lucide-react';
import { toast } from 'react-toastify';
import { supportService, SupportTicket, TicketStatus, TicketPriority, TicketFilters } from '../../services/api/support.service';
import { useWindow } from '../../context/WindowContext';
import { useSupport } from '../../context/SupportContext';

export default function Support() {
  const navigate = useNavigate();
  const { windowWidth } = useWindow();
  const { refreshUnreadCount } = useSupport();
  const isMobile = windowWidth < 640;
  
  // States
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchText, setSearchText] = useState('');
  
  // Filter states
  const [selectedStatus, setSelectedStatus] = useState<TicketStatus | ''>('');
  const [selectedPriority, setSelectedPriority] = useState<TicketPriority | ''>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Create ticket states
  const [newTicket, setNewTicket] = useState({
    type: 'SUPPORT' as const,
    subject: '',
    content: '',
    priority: 'MEDIUM' as TicketPriority
  });
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [filePreviews, setFilePreviews] = useState<{ [key: string]: string }>({});

  const limit = 20;

  // Load tickets
  const loadTickets = async () => {
    try {
      setLoading(true);
      
      const filters: TicketFilters = {
        limit,
        offset: (currentPage - 1) * limit,
        ...(selectedStatus && { status: selectedStatus }),
        ...(selectedPriority && { priority: selectedPriority }),
        ...(dateFrom && { dateFrom }),
        ...(dateTo && { dateTo })
      };

      const response = await supportService.getUserTickets(filters);
      
      if (response.tickets) {
        setTickets(response.tickets);
        setTotal(response.total);
        setHasMore(response.hasMore);
      }
    } catch (error) {
      console.error('Error loading tickets:', error);
      toast.error('Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTickets();
  }, [currentPage, selectedStatus, selectedPriority, dateFrom, dateTo]);

  // Auto-refresh tickets every 30 seconds when on this page
  useEffect(() => {
    const interval = setInterval(() => {
      loadTickets();
      refreshUnreadCount(); // Also refresh unread count
    }, 30000); // 30 seconds

    // Load on mount
    refreshUnreadCount();

    return () => clearInterval(interval);
  }, [refreshUnreadCount]);

  // Filter tickets by search text
  const filteredTickets = tickets.filter(ticket => 
    searchText === '' || 
    ticket.subject.toLowerCase().includes(searchText.toLowerCase()) ||
    ticket.content.toLowerCase().includes(searchText.toLowerCase()) ||
    ticket.ticketNumber.toLowerCase().includes(searchText.toLowerCase())
  );

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;
    processFiles(Array.from(files));
  };

  // Process files (from input or drop)
  const processFiles = (files: File[]) => {
    const validFiles: File[] = [];

    files.forEach(file => {
      // Check file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 5MB limit`);
        return;
      }

      // Check file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf',
                           'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name} has invalid format`);
        return;
      }

      validFiles.push(file);
    });

    // Max 5 files
    if (attachedFiles.length + validFiles.length > 5) {
      toast.error('Maximum 5 files allowed');
      return;
    }

    // Generate previews for images
    validFiles.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFilePreviews(prev => ({
            ...prev,
            [file.name]: e.target?.result as string
          }));
        };
        reader.readAsDataURL(file);
      }
    });

    setAttachedFiles([...attachedFiles, ...validFiles]);
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  };

  // Remove attached file
  const removeFile = (index: number) => {
    const fileToRemove = attachedFiles[index];
    setAttachedFiles(attachedFiles.filter((_, i) => i !== index));

    // Remove preview
    if (filePreviews[fileToRemove.name]) {
      setFilePreviews(prev => {
        const newPreviews = { ...prev };
        delete newPreviews[fileToRemove.name];
        return newPreviews;
      });
    }
  };

  // Create new ticket
  const handleCreateTicket = async () => {
    // Validate
    if (newTicket.subject.length < 5) {
      toast.error('Subject must be at least 5 characters');
      return;
    }
    if (newTicket.subject.length > 100) {
      toast.error('Subject must be less than 100 characters');
      return;
    }
    // Allow creating tickets with only files, no minimum content length required
    if (newTicket.content.length > 2000) {
      toast.error('Description must be less than 2000 characters');
      return;
    }
    
    try {
      setSubmitting(true);
      
      let response;
      if (attachedFiles.length > 0) {
        response = await supportService.createTicketWithAttachments(
          newTicket,
          attachedFiles
        );
      } else {
        response = await supportService.createTicket(newTicket);
      }
      
      if (response.success) {
        toast.success('Support ticket created successfully');
        setShowCreateModal(false);
        setNewTicket({ type: 'SUPPORT', subject: '', content: '', priority: 'MEDIUM' });
        setAttachedFiles([]);
        setFilePreviews({});
        loadTickets();
        
        // Navigate to ticket detail
        if (response.data?.ticket?.id) {
          navigate(`/dashboard/support/${response.data.ticket.id}`);
        }
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error('Failed to create support ticket');
    } finally {
      setSubmitting(false);
    }
  };

  // Get status badge style
  const getStatusBadge = (status: TicketStatus) => {
    const color = supportService.getStatusColor(status);
    return (
      <span 
        className="px-2 py-1 rounded-full text-xs font-medium"
        style={{ 
          backgroundColor: `${color}20`,
          color: color,
          border: `1px solid ${color}40`
        }}
      >
        {status.replace('_', ' ')}
      </span>
    );
  };

  // Get priority badge style
  const getPriorityBadge = (priority: TicketPriority) => {
    const color = supportService.getPriorityColor(priority);
    return (
      <span 
        className="px-2 py-1 rounded-full text-xs font-medium"
        style={{ 
          backgroundColor: `${color}20`,
          color: color,
          border: `1px solid ${color}40`
        }}
      >
        {priority}
      </span>
    );
  };

  return (
    <div className="p-3 sm:px-10 h-full max-w-full">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-4xl mb-2 font-medium">Support Center</h1>
        <p className="text-xl text-gray-600">Get help with your account</p>
      </div>

      {/* Create Button & Filters */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-6 py-3 bg-belobaba-gold text-white rounded-xl hover:opacity-90 transition-opacity font-medium"
        >
          <Plus size={20} />
          Create New Ticket
        </button>

        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          {/* Search */}
          <div className="relative flex-1 sm:flex-initial">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search tickets..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-belobaba-gold w-full sm:w-64"
            />
          </div>

          {/* Filter Button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Filter size={18} />
            Filters
            {(selectedStatus || selectedPriority || dateFrom || dateTo) && (
              <span className="w-2 h-2 bg-belobaba-gold rounded-full"></span>
            )}
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-gray-50 rounded-xl p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value as TicketStatus | '')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-belobaba-gold"
              >
                <option value="">All Status</option>
                <option value="PENDING">Pending</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="RESOLVED">Resolved</option>
                <option value="CLOSED">Closed</option>
                <option value="REJECTED">Rejected</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={selectedPriority}
                onChange={(e) => setSelectedPriority(e.target.value as TicketPriority | '')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-belobaba-gold"
              >
                <option value="">All Priorities</option>
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="CRITICAL">Critical</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-belobaba-gold"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-belobaba-gold"
              />
            </div>
          </div>

          <button
            onClick={() => {
              setSelectedStatus('');
              setSelectedPriority('');
              setDateFrom('');
              setDateTo('');
            }}
            className="mt-4 text-sm text-gray-600 hover:text-belobaba-gold transition-colors"
          >
            Clear Filters
          </button>
        </div>
      )}

      {/* Tickets List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-belobaba-gold"></div>
            <p className="mt-2 text-gray-600">Loading tickets...</p>
          </div>
        ) : filteredTickets.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center">
            <AlertCircle className="mx-auto text-gray-400 mb-4" size={48} />
            <h3 className="text-xl font-medium text-gray-700 mb-2">No tickets found</h3>
            <p className="text-gray-500">
              {searchText || selectedStatus || selectedPriority 
                ? "Try adjusting your filters"
                : "Create your first support ticket to get help"}
            </p>
          </div>
        ) : (
          filteredTickets.map((ticket) => (
            <div
              key={ticket.id}
              onClick={() => navigate(`/dashboard/support/${ticket.id}`)}
              className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow cursor-pointer border border-gray-100"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-sm text-gray-500">#{ticket.ticketNumber}</span>
                    {getStatusBadge(ticket.status)}
                    {getPriorityBadge(ticket.priority)}
                  </div>
                  <h3 className="text-lg font-medium text-gray-800 mb-1">{ticket.subject}</h3>
                  <p className="text-gray-600 line-clamp-2">{ticket.content}</p>
                </div>
                <ChevronRight className="text-gray-400 flex-shrink-0 ml-4" size={20} />
              </div>
              
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <div className="flex items-center gap-1">
                  <Clock size={14} />
                  {supportService.formatRelativeTime(ticket.createdAt)}
                </div>
                {ticket._count && ticket._count.messages > 0 && (
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                    {ticket._count.messages} message{ticket._count.messages > 1 ? 's' : ''}
                  </span>
                )}
                {ticket._count && ticket._count.attachments > 0 && (
                  <span className="flex items-center gap-1">
                    <Paperclip size={14} />
                    {ticket._count.attachments}
                  </span>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {total > limit && (
        <div className="flex justify-center items-center gap-4 mt-8">
          <button
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
          >
            Previous
          </button>
          
          <span className="text-gray-600">
            Page {currentPage} of {Math.ceil(total / limit)}
          </span>
          
          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={!hasMore}
            className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
          >
            Next
          </button>
        </div>
      )}

      {/* Create Ticket Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`bg-white rounded-xl ${isMobile ? 'w-full h-full' : 'max-w-2xl w-full max-h-[90vh]'} overflow-y-auto`}>
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-medium">Create Support Ticket</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewTicket({ type: 'SUPPORT', subject: '', content: '', priority: 'MEDIUM' });
                  setAttachedFiles([]);
                  setFilePreviews({});
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6">
              {/* Subject */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                  placeholder="Brief description of your issue"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-belobaba-gold"
                  maxLength={100}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {newTicket.subject.length}/100 characters
                </p>
              </div>

              {/* Priority */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                <div className="flex gap-2">
                  {(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as TicketPriority[]).map((priority) => (
                    <button
                      key={priority}
                      onClick={() => setNewTicket({ ...newTicket, priority })}
                      className={`px-4 py-2 rounded-lg border transition-colors ${
                        newTicket.priority === priority
                          ? 'bg-belobaba-gold text-white border-belobaba-gold'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {priority}
                    </button>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={newTicket.content}
                  onChange={(e) => setNewTicket({ ...newTicket, content: e.target.value })}
                  placeholder="Describe your issue in detail..."
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-belobaba-gold resize-none"
                  maxLength={2000}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {newTicket.content.length}/2000 characters
                </p>
              </div>

              {/* Attachments - Now Enabled with Drag & Drop */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Attachments (optional)
                  <span className="text-xs text-gray-500 ml-2">Max 5 files, 5MB each</span>
                </label>

                {/* Drag & Drop Zone */}
                <div
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    isDragging
                      ? 'border-belobaba-gold bg-yellow-50'
                      : 'border-gray-300 bg-gray-50 hover:border-belobaba-gold'
                  }`}
                >
                  <input
                    type="file"
                    multiple
                    accept="image/*,.pdf,.doc,.docx"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="file-upload-create"
                  />
                  <label
                    htmlFor="file-upload-create"
                    className="cursor-pointer inline-flex flex-col items-center gap-2"
                  >
                    <Paperclip size={24} className="text-gray-400" />
                    <span className="text-gray-600">
                      {isDragging ? 'Drop files here' : 'Click to browse or drag and drop'}
                    </span>
                    <span className="text-xs text-gray-500">
                      Images, PDFs, Word documents
                    </span>
                  </label>
                </div>

                {/* Attached Files List with Previews */}
                {attachedFiles.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {attachedFiles.map((file, index) => (
                      <div key={index} className="flex items-center gap-3 bg-white border border-gray-200 px-3 py-2 rounded-lg">
                        {/* Preview thumbnail for images */}
                        {filePreviews[file.name] ? (
                          <img
                            src={filePreviews[file.name]}
                            alt={file.name}
                            className="w-12 h-12 object-cover rounded"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                            <Paperclip size={20} className="text-gray-400" />
                          </div>
                        )}

                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-700 truncate">{file.name}</p>
                          <p className="text-xs text-gray-500">
                            {supportService.formatFileSize(file.size)}
                          </p>
                        </div>

                        <button
                          onClick={() => removeFile(index)}
                          className="text-red-500 hover:text-red-700 flex-shrink-0"
                          title="Remove file"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewTicket({ type: 'SUPPORT', subject: '', content: '', priority: 'MEDIUM' });
                    setAttachedFiles([]);
                    setFilePreviews({});
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateTicket}
                  disabled={submitting || !newTicket.subject}
                  className="px-6 py-2 bg-belobaba-gold text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {submitting && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  {submitting ? 'Creating...' : 'Submit Ticket'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}